import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Fullscreen as FullscreenIcon
} from '@mui/icons-material';
import { PurchaseService } from '../../services/purchaseService';
import { PurchaseOrder } from '../../types/purchase';

interface PurchaseOrderPDFPreviewProps {
  purchaseOrder: PurchaseOrder;
}

const PurchaseOrderPDFPreview: React.FC<PurchaseOrderPDFPreviewProps> = ({ purchaseOrder }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generatePDF = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Revoke previous URL if exists
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      
      // Use TallyPrime template by default
      const blob = await PurchaseService.generatePurchaseOrderPDF(purchaseOrder.id, 'tally');
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error: any) {
      console.error('Failed to generate PDF preview:', error);
      setError(error.response?.data?.detail || 'Failed to generate PDF preview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generatePDF();
    
    // Cleanup function to revoke object URL
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [purchaseOrder.id]);

  const handleRefresh = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    generatePDF();
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${purchaseOrder.poNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleFullscreen = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            PDF Preview
          </Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh Preview">
              <span>
                <IconButton onClick={handleRefresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            
            <Tooltip title="Open in New Tab">
              <span>
                <IconButton onClick={handleFullscreen} disabled={!pdfUrl || loading}>
                  <FullscreenIcon />
                </IconButton>
              </span>
            </Tooltip>
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={!pdfUrl || loading}
              size="small"
            >
              Download
            </Button>
          </Box>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button onClick={handleRefresh} size="small" sx={{ ml: 1 }}>
              Retry
            </Button>
          </Alert>
        )}

        {!loading && !error && pdfUrl && (
          <Box
            sx={{
              width: '100%',
              height: '600px',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              overflow: 'hidden'
            }}
          >
            <iframe
              src={pdfUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title={`Purchase Order ${purchaseOrder.poNumber} PDF Preview`}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PurchaseOrderPDFPreview;
